import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { AppBar } from './AppBar';
import { Sidebar, SIDEBAR_WIDTH_COLLAPSED, SIDEBAR_WIDTH_EXPANDED } from './Sidebar';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { useAuth } from '~/hooks/useAuth';
import { useCurrentUserProfile } from '~/hooks/useCurrentUserProfile';
import { getAxiosStatus, logoutApp } from '~/lib/auth/logout';
import { buildPermissionSet, CN01_PLANTER_DASHBOARD_PERMISSIONS, hasAnyPermission } from '~/lib/permissions';
import keycloak from '~/shared/guards/Keycloak';

interface ProtectedLayoutProps {
  children: ReactNode;
}

function getProfileLoadErrorMessage(status?: number): string {
  if (status === 404) {
    return 'We are currently unable to retrieve your account information. Please try again later or contact the system administrator if the issue persists.';
  }
  return 'We are currently unable to retrieve your account information. Please try again later or contact the system administrator if the issue persists.';
}

export function ProtectedLayout({ children }: ProtectedLayoutProps) {
  const { initialized, isAuthenticated } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const handleToggleSidebar = useCallback(() => {
    setSidebarOpen((s) => !s);
  }, []);

  const profileQuery = useCurrentUserProfile({
    enabled: Boolean(initialized && isAuthenticated),
  });

  const isPlanter = useMemo(
    () =>
      hasAnyPermission(
        buildPermissionSet(profileQuery.data?.permissions),
        CN01_PLANTER_DASHBOARD_PERMISSIONS
      ),
    [profileQuery.data?.permissions]
  );

  useEffect(() => {
    if (initialized && !isAuthenticated) {
      keycloak.login();
    }
  }, [initialized, isAuthenticated]);

  useEffect(() => {
    if (!profileQuery.isError || !profileQuery.error) return;
    const status = getAxiosStatus(profileQuery.error);
    if (status === 401 || status === 403) {
      logoutApp();
    }
  }, [profileQuery.isError, profileQuery.error]);

  if (!initialized || !isAuthenticated) {
    return (
      <Box
        sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <LoadingSpinner />
      </Box>
    );
  }

  if (profileQuery.isPending || profileQuery.isLoading) {
    return (
      <Box
        sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <LoadingSpinner />
      </Box>
    );
  }

  if (profileQuery.isError) {
    const status = getAxiosStatus(profileQuery.error);
    if (status === 401 || status === 403) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LoadingSpinner />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Stack spacing={2} sx={{ maxWidth: 420 }} alignItems="center">
          <Typography variant="h6">Unable to Access Your Account</Typography>
          <Alert severity="error" sx={{ width: '100%' }}>
            {getProfileLoadErrorMessage(status)}
          </Alert>
          <Button variant="contained" onClick={() => profileQuery.refetch()}>
            Retry
          </Button>
          <Button variant="text" onClick={() => logoutApp()}>
            Sign out
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar onToggleSidebar={handleToggleSidebar} />
      <Sidebar open={sidebarOpen} />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: '64px',
          width: isPlanter
            ? '100%'
            : `calc(100% - ${sidebarOpen ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED}px)`,
          transition: (theme) =>
            theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 1200 }}>{children}</Box>
        </Box>
      </Box>
    </Box>
  );
}
